/**
 * ⚡ Smart Replicate Modal
 * Konfiguration für intelligente Segment-Replikation
 * Unterstützt SMTP und Webmail Versand
 */

import React, { useState } from 'react';
import { X, Shuffle } from 'lucide-react';

const SmartReplicateModal = ({ isOpen, onClose, onConfirm, segmentCount, sendMethod = 'smtp' }) => {
  const isWebmail = sendMethod === 'webmail';
  
  const [targetThroughput, setTargetThroughput] = useState('');
  const [dynamicEmailMode, setDynamicEmailMode] = useState('short_codes');
  const [dynamicEmailPercentage, setDynamicEmailPercentage] = useState(70);
  const [fingerprintRotation, setFingerprintRotation] = useState('per_slot');
  const [ehloRotation, setEhloRotation] = useState('per_slot');
  const [encodingDistribution, setEncodingDistribution] = useState({
    'quoted-printable': 50,
    'base64': 30,
    '7bit': 20
  });
  const [useMultipart, setUseMultipart] = useState(true);
  const [multipartPercentage, setMultipartPercentage] = useState(70);
  const [clientDistribution, setClientDistribution] = useState({
    outlook_web: 25,
    gmail_web: 20,
    iphone_mail: 18,
    android_gmail: 15,
    thunderbird: 10,
    apple_mail: 7,
    clean: 5
  });
  
  if (!isOpen) return null;
  
  const handleConfirm = () => {
    const options = {
      target_throughput: isWebmail ? null : (parseInt(targetThroughput) || null),
      dynamic_email_mode: isWebmail ? 'none' : dynamicEmailMode,
      dynamic_email_percentage: isWebmail ? 0 : dynamicEmailPercentage,
      fingerprint_rotation: isWebmail ? null : fingerprintRotation,
      ehlo_rotation: isWebmail ? null : ehloRotation,
      encoding_distribution: isWebmail ? null : encodingDistribution,
      use_multipart: isWebmail ? false : useMultipart,
      multipart_percentage: isWebmail ? 0 : multipartPercentage,
      client_distribution: isWebmail ? null : clientDistribution,
      // Webmail-spezifisch
      is_webmail: isWebmail
    };
    
    onConfirm(options);
  };
  
  const perSegmentRate = targetThroughput ? Math.round(parseInt(targetThroughput) / segmentCount) : null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-dark-card rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center">
              <Shuffle className="h-6 w-6 mr-2 text-purple-600" />
              ⚡ Smart Replicate Optionen
              {isWebmail && <span className="ml-2 text-sm font-normal text-teal-600">(Webmail)</span>}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          
          {/* Webmail-Info Banner */}
          {isWebmail && (
            <div className="mb-6 p-4 bg-teal-50 dark:bg-teal-900/20 rounded-lg border border-teal-200 dark:border-teal-700">
              <div className="flex items-start space-x-3">
                <span className="text-2xl">📧</span>
                <div>
                  <h4 className="font-semibold text-teal-800 dark:text-teal-200">Webmail-Modus aktiv</h4>
                  <p className="text-sm text-teal-700 dark:text-teal-300 mt-1">
                    Bei Webmail-Versand werden SMTP-spezifische Optionen (Fingerprint, Encoding, EHLO, etc.) 
                    nicht verwendet. Die Segmente werden nur mit AI-Content randomisiert.
                  </p>
                  <ul className="text-xs text-teal-600 dark:text-teal-400 mt-2 space-y-1">
                    <li>✅ AI-Content (Subject, Message, From-Name)</li>
                    <li>✅ Slot-Konfiguration (Größe, Anzahl)</li>
                    <li>⏱️ Feste Rate: ~5 Mails/min pro Account (12.5s Cooldown)</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
          
          <div className="space-y-6">
            {/* Durchsatz-Ziel - NUR bei SMTP */}
            {!isWebmail && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                🎯 Ziel-Durchsatz (optional)
              </label>
              <input
                type="number"
                value={targetThroughput}
                onChange={(e) => setTargetThroughput(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-dark-copper bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100 rounded-md"
                placeholder="z.B. 10000 für 10k Mails/h gesamt"
              />
              {perSegmentRate && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  📊 Pro Segment: ~{perSegmentRate.toLocaleString()}/h (variiert je nach Preset)
                </p>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Leer lassen für individuelle Random-Rates (±20%)
              </p>
            </div>
            )}
            
            {/* Dynamic From-Email Modus - NUR bei SMTP */}
            {!isWebmail && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                📧 Dynamic From-Email Modus
              </label>
              <select
                value={dynamicEmailMode}
                onChange={(e) => setDynamicEmailMode(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-dark-copper bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100 rounded-md mb-2"
              >
                <option value="none">Keine (Original SMTP From-Email)</option>
                <option value="prefix_only">Nur Prefix (z.B. "team", "service")</option>
                <option value="short_codes">Kurze Codes (z.B. "team.($)3Z($)")</option>
                <option value="varied">Variiert (Mix, kürzer)</option>
                <option value="name_based">👤 Namen-basiert (z.B. "j.peters", "johanna-müller") - Millionen Varianten</option>
              </select>
              <div className="flex items-center space-x-4 mb-2">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={dynamicEmailPercentage}
                  onChange={(e) => setDynamicEmailPercentage(parseInt(e.target.value))}
                  className="flex-1"
                  disabled={dynamicEmailMode === 'none'}
                />
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100 w-16">
                  {dynamicEmailPercentage}%
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {Math.round(segmentCount * (dynamicEmailPercentage / 100))} von {segmentCount} Segmenten nutzen Dynamic From-Email
              </p>
            </div>
            )}
            
            {/* Mail-Fingerprint - NUR bei SMTP */}
            {!isWebmail && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                🎭 Mail-Fingerprint
              </label>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                    Fingerprint Rotation:
                  </label>
                  <select
                    value={fingerprintRotation}
                    onChange={(e) => setFingerprintRotation(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-dark-copper bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100 rounded-md text-sm"
                  >
                    <option value="per_segment">Pro Segment</option>
                    <option value="per_slot">Pro Slot</option>
                    <option value="per_mail">Pro Mail</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                    EHLO/HELO:
                  </label>
                  <select
                    value={ehloRotation}
                    onChange={(e) => setEhloRotation(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-dark-copper bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100 rounded-md text-sm"
                  >
                    <option value="per_segment">Pro Segment</option>
                    <option value="per_slot">Pro Slot</option>
                    <option value="per_mail">Pro Mail</option>
                  </select>
                </div>
              </div>
              
              
              {/* Encoding-Verteilung */}
              <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded">
                <div className="text-xs font-semibold mb-2 text-gray-700 dark:text-gray-200">
                  📊 Encoding-Verteilung:
                </div>
                
                {/* Quoted-Printable */}
                <div className="mb-3">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs text-gray-600 dark:text-gray-400">quoted-printable (Standard):</span>
                    <span className="text-xs font-medium text-gray-900 dark:text-gray-100">{encodingDistribution['quoted-printable']}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={encodingDistribution['quoted-printable']}
                    onChange={(e) => {
                      const val = parseInt(e.target.value);
                      const remaining = 100 - val;
                      const base64Share = Math.floor(remaining * 0.6);
                      const sevenBitShare = remaining - base64Share;
                      setEncodingDistribution({
                        'quoted-printable': val,
                        'base64': base64Share,
                        '7bit': sevenBitShare
                      });
                    }}
                    className="w-full"
                  />
                </div>
                
                {/* Base64 */}
                <div className="mb-3">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs text-gray-600 dark:text-gray-400">base64 (Gmail-Style):</span>
                    <span className="text-xs font-medium text-gray-900 dark:text-gray-100">{encodingDistribution['base64']}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={encodingDistribution['base64']}
                    onChange={(e) => {
                      const val = parseInt(e.target.value);
                      const remaining = 100 - val;
                      const qpShare = Math.floor(remaining * 0.7);
                      const sevenBitShare = remaining - qpShare;
                      setEncodingDistribution({
                        'quoted-printable': qpShare,
                        'base64': val,
                        '7bit': sevenBitShare
                      });
                    }}
                    className="w-full"
                  />
                </div>
                
                {/* 7bit */}
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs text-gray-600 dark:text-gray-400">7bit (iPhone-Style):</span>
                    <span className="text-xs font-medium text-gray-900 dark:text-gray-100">{encodingDistribution['7bit']}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={encodingDistribution['7bit']}
                    onChange={(e) => {
                      const val = parseInt(e.target.value);
                      const remaining = 100 - val;
                      const qpShare = Math.floor(remaining * 0.7);
                      const base64Share = remaining - qpShare;
                      setEncodingDistribution({
                        'quoted-printable': qpShare,
                        'base64': base64Share,
                        '7bit': val
                      });
                    }}
                    className="w-full"
                  />
                </div>
                
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Summe: {encodingDistribution['quoted-printable'] + encodingDistribution['base64'] + encodingDistribution['7bit']}%
                </div>
              </div>
              
              
              {/* Multipart/Alternative */}
              <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 rounded border border-green-200 dark:border-green-700">
                <label className="flex items-center space-x-2 cursor-pointer mb-2">
                  <input
                    type="checkbox"
                    checked={useMultipart}
                    onChange={(e) => setUseMultipart(e.target.checked)}
                    className="h-4 w-4 text-green-600 border-gray-300 rounded"
                  />
                  <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">
                    📧 Multipart/Alternative (Text + HTML)
                  </span>
                </label>
                
                {useMultipart && (
                  <div>
                    <div className="flex items-center space-x-4 mb-1">
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={multipartPercentage}
                        onChange={(e) => setMultipartPercentage(parseInt(e.target.value))}
                        className="flex-1"
                      />
                      <span className="text-xs font-medium text-gray-900 dark:text-gray-100 w-12">
                        {multipartPercentage}%
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {multipartPercentage}% als multipart (authentischer für Gmail/Outlook)
                    </div>
                  </div>
                )}
              </div>
              
              
              {/* Client-Type Verteilung */}
              <div className="mt-4 p-3 bg-purple-50 dark:bg-purple-900/20 rounded border border-purple-200 dark:border-purple-700">
                <div className="text-xs font-semibold mb-2 text-gray-700 dark:text-gray-200">
                  🎯 Client-Type Verteilung:
                </div>
                
                {Object.entries(clientDistribution).map(([client, percentage]) => (
                  <div key={client} className="mb-2">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        {client === 'outlook_web' && '💼 Outlook Web'}
                        {client === 'gmail_web' && '📧 Gmail Web'}
                        {client === 'iphone_mail' && '📱 iPhone Mail'}
                        {client === 'android_gmail' && '🤖 Android Gmail'}
                        {client === 'thunderbird' && '🦅 Thunderbird'}
                        {client === 'apple_mail' && '🍎 Apple Mail'}
                        {client === 'clean' && '🧹 Clean (keine Header)'}
                      </span>
                      <span className="text-xs font-medium text-gray-900 dark:text-gray-100">{percentage}%</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={percentage}
                      onChange={(e) => {
                        const newVal = parseInt(e.target.value);
                        const diff = newVal - percentage;
                        const others = Object.keys(clientDistribution).filter(k => k !== client);
                        const perOther = Math.floor(-diff / others.length);
                        
                        const updated = { ...clientDistribution, [client]: newVal };
                        others.forEach(k => {
                          updated[k] = Math.max(0, updated[k] + perOther);
                        });
                        
                        setClientDistribution(updated);
                      }}
                      className="w-full h-1"
                    />
                  </div>
                ))}
                
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Summe: {Object.values(clientDistribution).reduce((a, b) => a + b, 0)}%
                </div>
              </div>
            </div>
            )}
            
            {/* Info Box */}
            <div className={`${isWebmail ? 'bg-teal-50 dark:bg-teal-900/20 border-teal-200 dark:border-teal-700' : 'bg-blue-50 dark:bg-dark-surface border-blue-200 dark:border-dark-copper'} p-4 rounded-lg border`}>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Was wird randomisiert:
              </h4>
              <ul className="text-xs text-gray-700 dark:text-gray-200 space-y-1">
                <li>✅ AI-Content (Subject, Message, From-Name) mit Claude paraphrasiert</li>
                {isWebmail ? (
                  <>
                    <li>✅ Slot-Konfiguration (Größe, Anzahl pro Segment)</li>
                    <li>⏱️ Feste Rate: ~5 Mails/min pro Webmail-Account</li>
                    <li>🔄 12.5s Cooldown zwischen Batches</li>
                    <li>📧 Absender = Webmail-Account Email</li>
                  </>
                ) : (
                  <>
                    <li>✅ Rate-Limits ({perSegmentRate ? `Ziel: ${perSegmentRate}/h` : '±20% variiert'})</li>
                    <li>✅ Sending Patterns (8 verschiedene Typen)</li>
                    <li>✅ Streams (±1 Variation)</li>
                    <li>✅ Jitter (individuell randomisiert)</li>
                    <li>✅ Dynamic From-Emails ({dynamicEmailMode === 'name_based' ? 'Namen-basiert: Millionen Varianten (j.peters, johanna-mueller, etc.)' : 'verschiedene Patterns'})</li>
                    <li>✅ Mail-Fingerprint (Client-Type, Encoding, EHLO - {fingerprintRotation}/{ehloRotation})</li>
                  </>
                )}
              </ul>
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex justify-end space-x-3 mt-6 pt-4 border-t dark:border-dark-copper">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-dark-copper text-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-50 dark:hover:bg-dark-surface"
            >
              Abbrechen
            </button>
            <button
              onClick={handleConfirm}
              className={`px-4 py-2 ${isWebmail ? 'bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700' : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'} text-white rounded-md flex items-center space-x-2`}
            >
              <Shuffle className="h-4 w-4" />
              <span>Starten</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmartReplicateModal;
